if (!customElements.get('product-recommendation-carousel')) {
  customElements.define(
    'product-recommendation-carousel',
    class ProductRecommendationCarousel extends HTMLElement {
      constructor() {
        super();
        this.swiperContainer = this.querySelector('.product-recommendation__swiper');
        this.sliderInstance = null;
      }

      connectedCallback() {
        this.initSlider();
        this.bindVariantSelectors();
        this.bindAddToCartButtons();
      }

      initSlider() {
        if (!this.swiperContainer || typeof window.FoxTheme === 'undefined') return;

        const options = {
          slidesPerView: 1,
          spaceBetween: 0,
          loop: false,
          autoHeight: true,
        };

        // Con un solo producto Liquid no emite las flechas.
        const prevEl = this.querySelector('.product-recommendation__arrow--left');
        const nextEl = this.querySelector('.product-recommendation__arrow--right');
        if (prevEl && nextEl) {
          options.navigation = { prevEl, nextEl };
        }

        this.sliderInstance = new window.FoxTheme.Carousel(this.swiperContainer, options);
        this.sliderInstance.init();
      }

      bindVariantSelectors() {
        this.querySelectorAll('[data-variants-selectors]').forEach((container) => {
          container.querySelectorAll('select').forEach((select) => {
            select.addEventListener('change', () => this.onVariantChange(container));
          });
        });
      }

      onVariantChange(variantContainer) {
        const card = variantContainer.closest('.product-recommendation__card');
        if (!card) return;

        let variants;
        try {
          variants = JSON.parse(card.dataset.variantsJson);
        } catch (e) {
          return;
        }

        // data-variants-json only contains AVAILABLE variants, so any combination
        // that matches an entry is by definition purchasable.
        const selects = Array.from(variantContainer.querySelectorAll('select'));

        // Hide option values that aren't available for the current selection,
        // and reset any select whose chosen value just became invalid.
        this.refreshAvailableOptions(variantContainer, variants);

        // Collect option values by index: selects (visible) + hidden inputs (single-value options)
        const optionElements = Array.from(
          variantContainer.querySelectorAll('select, input[type="hidden"][data-option-index]')
        );
        const selected = [];
        optionElements.forEach((el) => {
          selected[parseInt(el.dataset.optionIndex, 10)] = el.value;
        });

        // Image updates as soon as ANY option changes: among the available
        // variants compatible with the options chosen so far, prefer one that
        // actually has an image (it may live on a single variant of the color).
        const compatible = variants.filter((v) =>
          v.options.every((opt, i) => !selected[i] || opt === selected[i])
        );
        const imageVariant =
          compatible.find((v) => v.featured_image && v.featured_image.src) || compatible[0];
        if (imageVariant) {
          this.updateImage(card, imageVariant);
        }

        // Reflect "a choice was made" styling on the selects.
        selects.forEach((s) => {
          s.style.color = s.value ? '#000' : '';
        });

        const atcButton = card.querySelector('[data-atc-button]');

        // Price + add-to-cart only update once every option is chosen.
        const fullySelected = optionElements.every((el) => el.value !== '');
        if (!fullySelected) {
          if (atcButton) atcButton.disabled = true;
          return;
        }

        const matchedVariant = variants.find((v) =>
          v.options.every((opt, i) => opt === selected[i])
        );

        if (!matchedVariant) {
          if (atcButton) atcButton.disabled = true;
          return;
        }

        if (atcButton) {
          atcButton.dataset.variantId = matchedVariant.id;
          atcButton.disabled = !matchedVariant.available;
        }

        const priceContainer = card.querySelector('[data-price-container]');
        if (priceContainer) {
          this.updatePrice(priceContainer, matchedVariant);
        }

        this.updateImage(card, matchedVariant);
      }

      refreshAvailableOptions(variantContainer, variants) {
        // Color leads the selection. The color selector always shows every color
        // (already globally filtered in Liquid). Every other selector only shows
        // values available for the currently chosen color.
        const colorSelect = variantContainer.querySelector('select[data-option-color]');
        if (!colorSelect) return; // no color selector -> nothing to filter

        const colorIdx = parseInt(colorSelect.dataset.optionIndex, 10);
        const colorValue = colorSelect.value;

        variantContainer.querySelectorAll('select[data-option-index]').forEach((select) => {
          if (select === colorSelect) return; // never filter the color selector

          const idx = parseInt(select.dataset.optionIndex, 10);
          let selectedStillValid = false;

          select.querySelectorAll('option').forEach((option) => {
            if (option.value === '') return; // keep the placeholder

            const available = variants.some((variant) => {
              if (variant.options[idx] !== option.value) return false;
              // Filter only by the chosen color.
              if (colorValue && variant.options[colorIdx] !== colorValue) return false;
              return true;
            });

            option.hidden = !available;
            option.disabled = !available;
            if (available && option.value === select.value) {
              selectedStillValid = true;
            }
          });

          // The previously selected size no longer exists for the chosen color.
          if (select.value && !selectedStillValid) {
            select.value = '';
          }
        });
      }

      updateImage(card, variant) {
        if (!variant.featured_image || !variant.featured_image.src) return;
        const img = card.querySelector('.product-recommendation__img');
        if (!img) return;
        img.src = this.sizeImageUrl(variant.featured_image.src, 112);
        img.srcset = '';
      }

      sizeImageUrl(src, width) {
        try {
          const url = new URL(src, window.location.origin);
          url.searchParams.set('width', String(width));
          return url.toString();
        } catch (e) {
          return src;
        }
      }

      updatePrice(container, variant) {
        const formatMoney = (cents) => {
          if (window.Shopify && window.Shopify.formatMoney) {
            return window.Shopify.formatMoney(cents);
          }
          return (
            '$' +
            (cents / 100).toLocaleString('es-AR', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })
          );
        };

        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          container.innerHTML =
            '<span class="product-recommendation__price-sale">' +
            formatMoney(variant.price) +
            '</span>' +
            '<span class="product-recommendation__price-compare"><s>' +
            formatMoney(variant.compare_at_price) +
            '</s></span>';
        } else {
          container.innerHTML =
            '<span class="product-recommendation__price-regular">' +
            formatMoney(variant.price) +
            '</span>';
        }
      }

      bindAddToCartButtons() {
        this.querySelectorAll('[data-atc-button]').forEach((button) => {
          button.addEventListener('click', (event) => {
            event.preventDefault();
            const variantId = parseInt(button.dataset.variantId, 10);
            if (!variantId || button.disabled) return;

            const spinner = button.querySelector('.loading__spinner');
            const icon = button.querySelector('.product-recommendation__plus-icon');
            if (spinner) spinner.classList.remove('hidden');
            if (icon) icon.classList.add('hidden');

            fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: variantId, quantity: 1 }),
            })
              .then((res) => res.json())
              .then(() => {
                document.dispatchEvent(
                  new CustomEvent('cart:refresh', { detail: { open: true } })
                );
              })
              .catch((err) => console.error('Add to cart failed:', err))
              .finally(() => {
                if (spinner) spinner.classList.add('hidden');
                if (icon) icon.classList.remove('hidden');
              });
          });
        });
      }
    }
  );
}
